import type { ProcessingDataset, ProcessingSource } from './processing-data';

const DATABASE_NAME = 'reportcrm-processing-datasets';
const DATABASE_VERSION = 1;
const STORE_NAME = 'datasets';
const MAX_CACHE_ENTRIES = 6;
const DATASET_FORMAT_VERSION = 2;

export const PROCESSING_DATASET_CACHE_MAX_AGE_MS = 6 * 60 * 60 * 1000;
export const RESPONSES_DATASET_CACHE_MAX_AGE_MS = 5 * 60 * 1000;

type CachedProcessingDataset = {
  key: string;
  userId: string;
  cachedAt: number;
  formatVersion: number;
  dataset: ProcessingDataset;
};

export type ProcessingDatasetCacheHit = {
  cachedAt: number;
  dataset: ProcessingDataset;
};

export function processingDatasetStorageKey(
  userId: string,
  source: ProcessingSource,
  year: number,
  month: number | null,
): string {
  return `${userId}:${source}:${year}:${month ?? 'latest'}`;
}

export function isProcessingDatasetCacheFresh(
  cachedAt: number,
  now = Date.now(),
  source: ProcessingSource = 'master',
): boolean {
  const maxAge = source === 'responses'
    ? RESPONSES_DATASET_CACHE_MAX_AGE_MS
    : PROCESSING_DATASET_CACHE_MAX_AGE_MS;
  return cachedAt > 0 && now - cachedAt < maxAge;
}

function openDatabase(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (database.objectStoreNames.contains(STORE_NAME)) return;
      const store = database.createObjectStore(STORE_NAME, { keyPath: 'key' });
      store.createIndex('cachedAt', 'cachedAt');
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Cache locale non disponibile.'));
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Operazione sulla cache locale non riuscita.'));
  });
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('Scrittura nella cache locale non riuscita.'));
    transaction.onabort = () => reject(transaction.error ?? new Error('Scrittura nella cache locale annullata.'));
  });
}

function validDataset(
  value: CachedProcessingDataset | undefined,
  source: ProcessingSource,
  year: number,
  month: number | null,
): value is CachedProcessingDataset {
  return Boolean(
    value
    && value.formatVersion === DATASET_FORMAT_VERSION
    && value.dataset?.source === source
    && value.dataset.year === year
    && value.dataset.month === month
    && Array.isArray(value.dataset.rows)
    && Array.isArray(value.dataset.columns),
  );
}

export async function readCachedProcessingDataset(input: {
  userId: string;
  source: ProcessingSource;
  year: number;
  month: number | null;
}): Promise<ProcessingDatasetCacheHit | null> {
  const database = await openDatabase();
  if (!database) return null;
  try {
    const key = processingDatasetStorageKey(input.userId, input.source, input.year, input.month);
    const transaction = database.transaction(STORE_NAME, 'readonly');
    const record = await requestResult(transaction.objectStore(STORE_NAME).get(key)) as CachedProcessingDataset | undefined;
    if (!validDataset(record, input.source, input.year, input.month)) return null;
    return { cachedAt: record.cachedAt, dataset: record.dataset };
  } finally {
    database.close();
  }
}

async function trimCache(database: IDBDatabase): Promise<void> {
  const transaction = database.transaction(STORE_NAME, 'readwrite');
  const store = transaction.objectStore(STORE_NAME);
  const count = await requestResult(store.count());
  let remaining = Math.max(0, count - MAX_CACHE_ENTRIES);
  if (remaining > 0) {
    await new Promise<void>((resolve, reject) => {
      const cursorRequest = store.index('cachedAt').openKeyCursor();
      cursorRequest.onerror = () => reject(cursorRequest.error ?? new Error('Pulizia della cache locale non riuscita.'));
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor || remaining === 0) {
          resolve();
          return;
        }
        store.delete(cursor.primaryKey);
        remaining -= 1;
        cursor.continue();
      };
    });
  }
  await transactionComplete(transaction);
}

export async function writeCachedProcessingDataset(input: {
  userId: string;
  dataset: ProcessingDataset;
}): Promise<void> {
  const database = await openDatabase();
  if (!database) return;
  try {
    const record: CachedProcessingDataset = {
      key: processingDatasetStorageKey(
        input.userId,
        input.dataset.source,
        input.dataset.year,
        input.dataset.month,
      ),
      userId: input.userId,
      cachedAt: Date.now(),
      formatVersion: DATASET_FORMAT_VERSION,
      dataset: input.dataset,
    };
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).put(record);
    await transactionComplete(transaction);
    await trimCache(database);
  } finally {
    database.close();
  }
}
